import type { AxiosError } from 'axios';

import { getFundingAccountTrackingFields } from 'components/Transfer/fundingAccountPolicy';
import { FE_SESSION_ID_KEY } from 'constants/session';
import { parseError } from 'lib/api/errors/parseError';
import { exchangeApiV1 } from 'lib/api/exchangeApi';
import { selectPerpsOnlyStatus } from 'lib/perpsOnlyStatus';
import { useAuthStore } from 'lib/stores/authStore';
import type { ApiErrorType } from 'models';
import {
    type ClientEvent,
    type ClientEventAttibutionAnswer,
    type ClientEventCommonProperties,
    type ClientEventExploreEngagement,
    type ClientEventPushNotificationPermissionAction,
    type ClientEventSearchQuery,
    type ClientEventSearchSession,
    type ClientEventSearchResultClick,
    type ClientEventTradingPageVisit,
    type ClientEventWatchlistUpdate,
    type SignUpError,
    type SignUpStep,
    type DepositStep,
    type ClientEventSportsEngagement,
    type ClientEventPageLoad,
    type ClientEventImpression,
    type ClientEventClick,
    type ClientEventDepositImpression,
    type ClientEventOrder,
    type ClientEventSignupView,
    type ClientEventSignupClick,
    type ClientEventDepositInitiated,
    type ClientEventPerpsFunding,
    type ClientEventPerpsFundingEventType,
    type ClientEventPerpsOnboarding,
    type ClientEventPerpsOnboardingEventType,
    type ClientEventPerpsTrading,
    type ClientEventPerpsTradingEventType,
    type ClientEventCarouselSwipe,
    type ClientEventPerpsMarketFaqClick,
    type ClientEventPerpsMarketFaqLearnMoreClick,
    type ClientEventPerpsMarketSocialClick,
    type ClientEventPerpsPredictionDataClick,
    type ClientEventSocialEngagement,
    type ClientEventShare,
    type ClientEventLiveChatReply,
    type PageType,
    type SearchFilterMetadata,
    resolvePageType,
    resolvePageName,
    resolvePageId,
} from 'models/metric';
import type { RecordClientEventRequest } from 'models/reqres';
import { getCountryCodeSnapshot, loadLocation } from 'queries/countryCode';
import { useAppNavigationStore } from 'stores/zustand/appNavigationStore';
import { useAttributionStore } from 'stores/zustand/attributionStore';
import { useEventPageUiStore } from 'stores/zustand/eventPageUiStore';
import { useMarketEntitiesStore } from 'stores/zustand/marketEntitiesStore';
import { useReferralStore } from 'stores/zustand/referralStore';
import { useTransferDialogStore } from 'stores/zustand/transferDialogStore';
import {
    sendEvent as sendAmplitudeEvent,
    setUserProperties as setAmplitudeUserProperties,
} from 'utils/analytics/providers/amplitude';
import config from 'utils/config';
import { getDeviceType, getOperatingSystem } from 'utils/device';
import { getErrorMessage } from 'utils/errors';
import logProviderFactory from 'utils/logger/logProviderFactory';
import { getRumContext } from 'utils/rumContext';
import { uuidv4 } from 'utils/uuid';

// ---------------------------------------------------------------------------
// Telemetry service module.
//
// Metrics is a pure side-effect sink: it only *sends* events (to Amplitude and
// the internal metrics backend). Nothing renders off it and nothing subscribes
// to it, so per CLAUDE.md's "Stores vs service modules" guidance it is a plain
// service module — not a Zustand store and not React Query.
//
// Ambient inputs (UTM data, current pathname / page-load id, orderbook
// settings, market entity resolution) are read directly from their zustand
// stores; The country code comes from queries/countryCode's
// RQ cache instead. Auth is read from `useAuthStore`.
// ---------------------------------------------------------------------------

type OrderbookSettingsForAnalytics = {
    showFullOrderbook?: boolean;
    showMakerOrTaker?: ClientEventPageLoad['orderbook_view'];
    showSellPrices?: boolean;
    headerExpanded?: boolean;
    collapseToCents?: boolean;
    hideFractionalShares?: boolean;
    showIncentiveDetails?: boolean;
};

type OrderbookPageLoadProperties = Pick<
    ClientEventPageLoad,
    | 'orderbook_view'
    | 'orderbook_show_full_orderbook'
    | 'orderbook_show_sell_prices'
    | 'orderbook_header_expanded'
    | 'orderbook_collapse_to_cents'
    | 'orderbook_hide_fractional_shares'
    | 'orderbook_show_incentive_details'
>;

function getOrderbookPageLoadProperties(settings?: OrderbookSettingsForAnalytics): OrderbookPageLoadProperties {
    return {
        orderbook_view: settings?.showMakerOrTaker ?? 'oneSided',
        orderbook_show_full_orderbook: settings?.showFullOrderbook ?? false,
        orderbook_show_sell_prices: settings?.showSellPrices ?? false,
        orderbook_header_expanded: settings?.headerExpanded ?? false,
        orderbook_collapse_to_cents: settings?.collapseToCents ?? false,
        orderbook_hide_fractional_shares: settings?.hideFractionalShares ?? false,
        orderbook_show_incentive_details: settings?.showIncentiveDetails ?? true,
    };
}

// Shared, mutable common-properties object. Fields are filled lazily on the
// first authenticated send and re-used by reference across event payloads,
// mirroring the original store's behavior.
const commonProperties: ClientEventCommonProperties = {
    platform: 'web',
    device: getDeviceType(),
    operating_system: getOperatingSystem(),
    country_code: '',
    user_id: useAuthStore.getState().userId ?? '',
    client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    app_version: config.commitRef,
};

let lastEntrypoint: string | undefined;
let pendingRedirect = false;
let pendingSearchEntrypoint = false;

/**
 * Seeds Amplitude user properties with the static device/session context. Called
 * once at app boot from the session-effects bridge. Idempotent — Amplitude
 * `identify` is safe to repeat.
 */
export function startMetrics(): void {
    setAmplitudeUserProperties({
        platform: 'web',
        device: getDeviceType(),
        operating_system: getOperatingSystem(),
        client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        app_version: config.commitRef,
    });
}

/** The shared common-properties object. Exposed for the rare caller that embeds it directly. */
export function getCommonProperties(): ClientEventCommonProperties {
    return commonProperties;
}

export function recordUserMetric(payload: RecordClientEventRequest, isRetry: boolean = false): void {
    const { userId, isLoggedIn } = useAuthStore.getState();
    commonProperties.device ??= getDeviceType();
    commonProperties.operating_system ??= getOperatingSystem();
    commonProperties.app_version ??= config.commitRef;
    commonProperties.timestamp = new Date().toISOString();
    commonProperties.session_id =
        typeof localStorage !== 'undefined' && typeof localStorage.getItem === 'function'
            ? localStorage.getItem(FE_SESSION_ID_KEY) || undefined
            : undefined;
    commonProperties.referrer = document?.referrer || undefined;

    if (!isRetry) {
        // Removing impression events from Amplitude to reduce event load.
        // These still get sent to our internal metrics backend below.
        const type = payload.event_data.event_type;
        const isImpressionEvent = type === 'impression' || type === 'deposit_impression';
        const isSignupEvent =
            type === 'signup_step' || type === 'signup_error' || type === 'signup_view' || type === 'signup_click';
        // Only send to Amplitude for authenticated users (plus signup events for anonymous users)
        if (!isImpressionEvent && (userId || isSignupEvent)) {
            sendAmplitudeEvent(type, payload.event_data.event_properties);
        }
    }

    if (!userId) {
        return;
    }

    commonProperties.user_id ||= userId;

    if (!commonProperties.country_code) {
        const countryCode = getCountryCodeSnapshot();
        if (!countryCode && !isRetry) {
            loadLocation(() => recordUserMetric(payload, true));
            return;
        }
        commonProperties.country_code = countryCode;
    }

    commonProperties.utm = useAttributionStore.getState().utmData;

    if (isLoggedIn) {
        // Fire-and-forget — the internal metrics endpoint's response is unused, but a
        // failure is still worth a warning so a broken metrics pipeline stays visible
        // without paging anyone (logWarning, not logError/console.error).
        void exchangeApiV1
            .post(`/users/${userId}/metrics/client_event`, payload)
            .catch((error: AxiosError<ApiErrorType>) => {
                const parsedError = parseError(error);
                if (
                    parsedError.cancelled ||
                    parsedError.timeout ||
                    parsedError.tooManyRequests ||
                    parsedError.notFound
                ) {
                    return;
                }
                logProviderFactory.logWarning(`Metrics event failed to record: ${getErrorMessage(parsedError)}`);
            });
    }
}

function buildSearchFilterMetadata(params: URLSearchParams): SearchFilterMetadata {
    return {
        query: params.get('q') ?? params.get('search') ?? undefined,
        status: params.get('status') ?? undefined,
        order_by: params.get('order_by') ?? undefined,
        category: params.get('category') ?? undefined,
        prop_only: params.get('props') === 'true',
        exclude_sports: params.get('excludeSports') === 'true',
        frequency: params.get('frequency') ?? undefined,
        reverse: params.get('reverse') === 'true',
    };
}

export function recordPageLoadMetric(eventProperties: ClientEventPageLoad): void {
    recordUserMetric({
        event_data: {
            event_type: 'page_load',
            event_properties: eventProperties,
            common_properties: commonProperties,
        },
    });
}

export function resolveEntrypoint(previousPathname: string, _currentPageType: PageType): string {
    // Popover search doesn't change the URL, so check if user clicked a search result
    if (pendingSearchEntrypoint) {
        pendingSearchEntrypoint = false;
        return 'search';
    }

    if (!previousPathname) return 'direct';

    const previousPageType = resolvePageType(previousPathname);

    // Series-only URLs redirect to full URLs (e.g. /markets/series → /markets/series/slug/event),
    // reuse the entrypoint from before the redirect
    if (pendingRedirect && lastEntrypoint) {
        pendingRedirect = false;
        return lastEntrypoint;
    }
    pendingRedirect = false;

    switch (previousPageType) {
        case 'search':
        case 'explore':
        case 'portfolio':
        case 'watchlist':
        case 'event':
            return previousPageType;
        case 'browse':
        case 'category':
        case 'sports':
            return 'browse';
        case 'ideas':
            return 'social';
        default:
            return 'other';
    }
}

export function recordPageLoadForPath(pathname: string, urlSearchParams: URLSearchParams): void {
    const pageLoadId = uuidv4();
    const pageType = resolvePageType(pathname);
    // Capture previous pathname before it gets updated by the next useEffect
    const entrypoint = resolveEntrypoint(useAppNavigationStore.getState().currentPathname, pageType);
    lastEntrypoint = entrypoint;
    useAppNavigationStore.getState().actions.setPageLoadId(pageLoadId);

    const sendPageLoad = () => {
        const pageName = resolvePageName(
            pathname,
            (eventTicker) => useMarketEntitiesStore.getState().events[eventTicker]?.title,
        );
        const pageId = resolvePageId(pathname, urlSearchParams);

        // Resolve market-specific fields from the store when on an event page
        let seriesTicker: string | undefined;
        let eventTicker: string | undefined;
        let eventCategory: string | undefined;
        let eventSubcategories: string[] | undefined;
        let orderbookPageLoadProperties: OrderbookPageLoadProperties | undefined;
        if (pageType === 'event') {
            orderbookPageLoadProperties = getOrderbookPageLoadProperties(
                useEventPageUiStore.getState().orderBookSettings,
            );

            const segments = pathname.split('/');
            seriesTicker = segments[2]?.toUpperCase() || undefined;
            const eventTickerFromUrl = segments[4]?.toUpperCase();
            if (eventTickerFromUrl) {
                const event = useMarketEntitiesStore.getState().events[eventTickerFromUrl];
                if (event) {
                    eventTicker = event.ticker;
                    seriesTicker = event.seriesTicker || seriesTicker;
                    const series = useMarketEntitiesStore.getState().series[event.seriesTicker];
                    eventCategory = series?.category || undefined;
                    eventSubcategories = series?.tags && series.tags.length > 0 ? series.tags : undefined;
                } else {
                    eventTicker = eventTickerFromUrl;
                }
            }
        }

        // Search filter context so open-vs-closed and props searches are distinguishable on page_load.
        const filter = pageType === 'search' ? buildSearchFilterMetadata(urlSearchParams) : undefined;

        recordPageLoadMetric({
            page_load_id: pageLoadId,
            page_type: pageType,
            page_name: pageName,
            page_id: pageId,
            pathname,
            filter,
            series_ticker: seriesTicker,
            event_ticker: eventTicker,
            entrypoint,
            event_category: eventCategory,
            event_subcategories: eventSubcategories,
            ...(orderbookPageLoadProperties ?? {}),
        });
    };

    const isSeriesOnlyEventUrl = pageType === 'event' && pathname.split('/').length < 5;
    if (isSeriesOnlyEventUrl) {
        // Series-only URLs redirect to full URLs — flag so the next page load
        // reuses this entrypoint instead of resolving to 'event'
        pendingRedirect = true;
        // Delay to allow redirects to settle
        setTimeout(sendPageLoad, 2000);
    } else {
        sendPageLoad();
    }
}

export function recordImpressionMetric(eventProperties: ClientEventImpression): void {
    sendAuthenticatedEvent('impression', eventProperties);
}

export function recordClickMetric(eventProperties: ClientEventClick): void {
    recordUserMetric({
        event_data: {
            event_type: 'click',
            event_properties: eventProperties,
            common_properties: commonProperties,
        },
    });
}

export function recordDepositImpression(eventProperties: ClientEventDepositImpression): void {
    recordUserMetric({
        event_data: {
            event_type: 'deposit_impression',
            event_properties: eventProperties,
            common_properties: commonProperties,
        },
    });
}

export function recordSportsEngagementMetric(eventProperties: ClientEventSportsEngagement): void {
    recordUserMetric({
        event_data: {
            event_type: 'sports_engagement',
            event_properties: eventProperties,
            common_properties: commonProperties,
        },
    });
}

export function recordDepositMetric(eventProperties: DepositStep): void {
    const accountContext = getFundingAccountTrackingFields(useTransferDialogStore.getState().depositDestination);

    recordUserMetric({
        event_data: {
            event_type: 'deposit_step',
            event_properties: {
                ...getRumContext(),
                ...accountContext,
                // Callers that snapshot funding context at flow/callback time
                // must win over the live dialog store, which may already have
                // reset by the time a terminal event is emitted.
                ...eventProperties,
            },
            common_properties: commonProperties,
        },
    });
}

export function recordSignUpMetric(eventProperties: SignUpStep): void {
    const referral = useReferralStore.getState().referral;
    const perpsOnlyStatus = selectPerpsOnlyStatus(useAuthStore.getState());
    recordUserMetric({
        event_data: {
            event_type: 'signup_step',
            event_properties: {
                ...getRumContext(),
                ...eventProperties,
                account_mode: perpsOnlyStatus === 'perpsOnly' ? 'perps_only' : perpsOnlyStatus,
                has_referral: !!referral,
                referral_code: referral,
            },
            common_properties: commonProperties,
        },
    });
}

export function recordSignUpError(eventProperties: SignUpError): void {
    recordUserMetric({
        event_data: {
            event_type: 'signup_error',
            event_properties: eventProperties,
            common_properties: commonProperties,
        },
    });
}

export function recordSignupView(eventProperties: ClientEventSignupView): void {
    recordUserMetric({
        event_data: {
            event_type: 'signup_view',
            event_properties: eventProperties,
            common_properties: commonProperties,
        },
    });
}

export function recordSignupClick(eventProperties: ClientEventSignupClick): void {
    recordUserMetric({
        event_data: {
            event_type: 'signup_click',
            event_properties: eventProperties,
            common_properties: commonProperties,
        },
    });
}

export function recordDepositInitiated(eventProperties: ClientEventDepositInitiated): void {
    sendAuthenticatedEvent('deposit_initiated', eventProperties);
}

export function recordPerpsFundingMetric(
    eventType: ClientEventPerpsFundingEventType,
    eventProperties: ClientEventPerpsFunding,
): void {
    recordUserMetric({
        event_data: {
            event_type: eventType,
            event_properties: { ...getRumContext(), ...eventProperties },
            common_properties: commonProperties,
        },
    });
}

export function recordPerpsOnboardingMetric(
    eventType: ClientEventPerpsOnboardingEventType,
    eventProperties: ClientEventPerpsOnboarding,
): void {
    recordUserMetric({
        event_data: {
            event_type: eventType,
            event_properties: { ...getRumContext(), ...eventProperties },
            common_properties: commonProperties,
        },
    });
}

export function recordPerpsTradingMetric(
    eventType: ClientEventPerpsTradingEventType,
    eventProperties: ClientEventPerpsTrading,
): void {
    recordUserMetric({
        event_data: {
            event_type: eventType,
            event_properties: { ...getRumContext(), ...eventProperties },
            common_properties: commonProperties,
        },
    });
}

export type RecordCarouselSwipeMetricArgs = {
    containerType: ClientEventCarouselSwipe['container_type'];
    containerId: ClientEventCarouselSwipe['container_id'];
    toIndex: ClientEventCarouselSwipe['to_index'];
};

export function recordCarouselSwipeMetric({
    containerId,
    containerType,
    toIndex,
}: RecordCarouselSwipeMetricArgs): void {
    sendAmplitudeEvent('carousel_swipe', {
        container_type: containerType,
        container_id: containerId,
        to_index: toIndex,
    });
}

export type RecordPerpsMarketSocialClickMetricArgs = {
    action: ClientEventPerpsMarketSocialClick['action'];
    marketTicker: ClientEventPerpsMarketSocialClick['market_ticker'];
    section: ClientEventPerpsMarketSocialClick['section'];
    surface: ClientEventPerpsMarketSocialClick['surface'];
};

export function recordPerpsMarketSocialClickMetric({
    action,
    marketTicker,
    section,
    surface,
}: RecordPerpsMarketSocialClickMetricArgs): void {
    sendAmplitudeEvent('perps_market_social_click', {
        surface,
        market_ticker: marketTicker,
        section,
        action,
    });
}

export type RecordPerpsMarketFaqClickMetricArgs = {
    marketTicker: ClientEventPerpsMarketFaqClick['market_ticker'];
    question: ClientEventPerpsMarketFaqClick['question'];
};

// Fired on expand only (not collapse), matching the mobile app.
export function recordPerpsMarketFaqClickMetric({ marketTicker, question }: RecordPerpsMarketFaqClickMetricArgs): void {
    sendAmplitudeEvent('perps_market_faq_click', {
        surface: 'perps_market',
        market_ticker: marketTicker,
        question,
    });
}

export type RecordPerpsMarketFaqLearnMoreClickMetricArgs = {
    marketTicker: ClientEventPerpsMarketFaqLearnMoreClick['market_ticker'];
};

export function recordPerpsMarketFaqLearnMoreClickMetric({
    marketTicker,
}: RecordPerpsMarketFaqLearnMoreClickMetricArgs): void {
    sendAmplitudeEvent('perps_market_faq_learn_more_click', {
        surface: 'perps_market',
        market_ticker: marketTicker,
    });
}

export type RecordPerpsPredictionDataClickMetricArgs = {
    eventTicker: ClientEventPerpsPredictionDataClick['event_ticker'];
    marketTicker: ClientEventPerpsPredictionDataClick['market_ticker'];
    surface: ClientEventPerpsPredictionDataClick['surface'];
    timeframe: ClientEventPerpsPredictionDataClick['timeframe'];
};

export function recordPerpsPredictionDataClickMetric({
    eventTicker,
    marketTicker,
    surface,
    timeframe,
}: RecordPerpsPredictionDataClickMetricArgs): void {
    sendAmplitudeEvent('perps_prediction_data_click', {
        event_ticker: eventTicker,
        market_ticker: marketTicker,
        surface,
        timeframe,
    });
}

export function recordSocialEngagementMetric(eventProperties: ClientEventSocialEngagement): void {
    sendAuthenticatedEvent('social_engagement', eventProperties);
}

export function recordShareMetric(eventProperties: ClientEventShare): void {
    sendAuthenticatedEvent('share', eventProperties);
}

export function recordLiveChatReplyMetric(
    eventType: 'live_chat_reply_long_press' | 'live_chat_reply_tap',
    eventProperties: ClientEventLiveChatReply,
): void {
    sendAuthenticatedEvent(eventType, eventProperties);
}

/**
 * Sends an event only when the user is authenticated (has a user_id).
 * Most user-initiated events use this to avoid recording events for logged-out users.
 */
function sendAuthenticatedEvent(
    eventType: ClientEvent['event_type'],
    eventProperties: ClientEvent['event_properties'],
): void {
    if (!useAuthStore.getState().userId) return;

    recordUserMetric({
        event_data: {
            event_type: eventType,
            event_properties: eventProperties,
            common_properties: commonProperties,
        },
    });
}

export function sendTradingPageVisit(eventProperties: ClientEventTradingPageVisit): void {
    sendAuthenticatedEvent('trading_page_visit', eventProperties);
}

export function sendExploreEngagement(eventProperties: ClientEventExploreEngagement, tracking_data: any): void {
    sendAuthenticatedEvent('explore_engagement', { ...tracking_data, ...eventProperties });
}

export function sendWatchlistUpdate(eventProperties: ClientEventWatchlistUpdate): void {
    sendAuthenticatedEvent('watchlist_update', eventProperties);
}

export function sendAttributionAnswer(eventProperties: ClientEventAttibutionAnswer): void {
    sendAuthenticatedEvent('attribution_answer', eventProperties);
}

export function sendPushNotificationPermissionEvent(
    eventProperties: ClientEventPushNotificationPermissionAction,
): void {
    sendAuthenticatedEvent('push_notification_action', eventProperties);
}

export function sendSearchQuery(eventProperties: ClientEventSearchQuery): void {
    sendAuthenticatedEvent('search_query', eventProperties);
}

export function sendSearchSession(eventProperties: ClientEventSearchSession): void {
    sendAuthenticatedEvent('search_session', eventProperties);
}

export function sendSearchResultClick(eventProperties: ClientEventSearchResultClick): void {
    if (!useAuthStore.getState().userId) return;

    // Flag so the next page load resolves entrypoint as 'search'
    // (popover search doesn't change the URL, so pathname-based resolution wouldn't work)
    pendingSearchEntrypoint = true;

    sendAuthenticatedEvent('search_result', eventProperties);
}

export function recordReviewOrder(eventProperties: ClientEventOrder): void {
    sendAuthenticatedEvent('review_order', eventProperties);
}

export function recordSubmitOrder(eventProperties: ClientEventOrder): void {
    sendAuthenticatedEvent('submit_order', eventProperties);
}
